/**
 * CurrentTimeLine
 * ──────────────────────────────────────────────────────────────────────
 * Persistent horizontal red line rendered across a day/week calendar at
 * the exact current time (Edit 2 of scheduler_edits_v2.md).
 *
 * Style per spec:
 *   - 1.5px solid #ef4444 (red)
 *   - Left cap: 6px filled red circle aligned to the gutter
 *   - Label: "HH:MM AM/PM" in 9px #ef4444 in the gutter just above the line
 *
 * The caller provides:
 *   - `dayStartHour` / `dayEndHour`: the rendered window (e.g. 8 → 18).
 *   - `gutterWidth`: the px width of the hour label gutter.
 *   - `hourPx`: the px height of a single hour slot.
 *   - `mode`: 'day' (line spans full width) or 'week' (line spans only the
 *             `columnWidthPct` slice anchored at `columnStartPct`).
 *
 * Returns null if `now` falls outside the rendered window — keeping the
 * line invisible on past/future days when used inside a parent that
 * decides day equality.
 */
import { useCurrentTime } from './useCurrentTime';

const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);
const formatLabel = (d) => {
  const h = d.getHours();
  const m = d.getMinutes();
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${pad2(m)} ${period}`;
};

const CurrentTimeLine = ({
  dayStartHour = 8,
  dayEndHour = 18,
  gutterWidth = 64,
  hourPx = 56,
  mode = 'day',
  columnStartPct = 0,
  columnWidthPct = 100,
  testId = 'current-time-line',
}) => {
  const now = useCurrentTime(60000);
  const hours = now.getHours() + now.getMinutes() / 60;
  if (hours < dayStartHour || hours > dayEndHour) return null;
  const top = (hours - dayStartHour) * hourPx;

  const leftPx = mode === 'day' ? gutterWidth : gutterWidth;
  const rightStyle =
    mode === 'week'
      ? { left: `calc(${gutterWidth}px + ${columnStartPct}% )`, width: `${columnWidthPct}%` }
      : { left: `${leftPx}px`, right: 0 };

  return (
    <>
      {/* Label in the gutter */}
      <div
        data-testid={`${testId}-label`}
        style={{
          position: 'absolute',
          top: top - 12,
          left: 0,
          width: gutterWidth,
          textAlign: 'right',
          paddingRight: 6,
          fontSize: 9,
          color: '#ef4444',
          pointerEvents: 'none',
        }}
      >
        {formatLabel(now)}
      </div>
      {/* Red dot cap */}
      <div
        data-testid={`${testId}-dot`}
        style={{
          position: 'absolute',
          top: top - 3,
          left: gutterWidth - 3,
          width: 6,
          height: 6,
          borderRadius: 999,
          background: '#ef4444',
          pointerEvents: 'none',
          zIndex: 5,
        }}
      />
      {/* Line */}
      <div
        data-testid={testId}
        style={{
          position: 'absolute',
          top: top,
          height: 1.5,
          background: '#ef4444',
          pointerEvents: 'none',
          zIndex: 4,
          ...rightStyle,
        }}
      />
    </>
  );
};

export default CurrentTimeLine;
